import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import type { ReviewToken } from '@sleed/bizcommerce-api';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { ProductThumb } from '~/components/catalog/bundles/product-combobox';
import { Pill } from '~/components/pill';
import { formatDate, formatDateTime } from '~/lib/format-date';
import { serverMessage } from '~/lib/api/server-error';
import { STOREFRONT_URL } from '~/lib/brand';
import {
	isProductTarget,
	reviewAuthorKind,
	reviewTargetName,
	reviewTargetTypeLabel,
	reviewTargetVariant,
	storefrontUrl
} from '~/lib/reviews/reviews';
import { useReviewInfo, type ReviewKind } from '~/hooks/use-reviews';
import { ReviewPhotoGallery } from './review-photos';
import { ReviewReportsSection } from './review-reports-section';

interface Props {
	kind: ReviewKind;
	/** The grid row (its photos); null closes the sheet. */
	row: ReviewToken | null;
	onOpenChange: (open: boolean) => void;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
	return (
		<div className="grid grid-cols-[160px_1fr] gap-3 py-1.5 text-sm">
			<span className="text-foreground-subtle">{label}</span>
			<span className="min-w-0 break-words text-foreground">{children ?? '—'}</span>
		</div>
	);
}

/**
 * A review's context, to judge it before approving: its photos and the reports customers sent, and GetReviewInfo (the
 * legacy "details" popup): the reviewed item of any type (code, link; category and publisher/manufacturer for a
 * product) and who wrote it — a customer (since when, how many orders and reviews) or a contributor.
 */
export function ReviewDetailsSheet({ kind, row, onOpenChange }: Props) {
	const { t } = useTranslation();
	const reviewId = row?.id ?? null;
	const { data: info, isLoading, isError, error } = useReviewInfo(reviewId);
	const targetUrl = storefrontUrl(STOREFRONT_URL, info?.to_url);

	return (
		<Sheet open={row != null} onOpenChange={onOpenChange}>
			<SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-lg">
				<SheetHeader className="border-b border-border px-5 py-4">
					<SheetTitle>{t('catalog.reviews.details.title')}</SheetTitle>
					<SheetDescription>{info?.to_nm ?? row?.to_title ?? ''}</SheetDescription>
				</SheetHeader>
				<div className="flex flex-col gap-5 overflow-y-auto px-5 py-4">
					{(row?.photos?.length ?? 0) > 0 && (
						<section>
							<h3 className="mb-2 text-xs font-semibold tracking-[0.04em] text-foreground-subtle">{t('catalog.reviews.photos.title')}</h3>
							<ReviewPhotoGallery key={reviewId ?? 0} photos={row?.photos} />
						</section>
					)}
					{reviewId != null && <ReviewReportsSection reviewId={reviewId} />}
					{isLoading ? (
						<Skeleton className="h-64 w-full rounded-md" />
					) : isError || !info ? (
						<p className="text-sm text-foreground-muted">{isError ? serverMessage(error) : t('common.noResults')}</p>
					) : (
						<>
							<section>
								<h3 className="mb-1 text-xs font-semibold tracking-[0.04em] text-foreground-subtle">
									{t('catalog.reviews.details.review')}
								</h3>
								<Row label={t('catalog.reviews.columns.date')}>{formatDateTime(info.rdt)}</Row>
								<Row label={t('catalog.reviews.columns.rating')}>{info.rt ?? '—'}</Row>
								<Row label={t('catalog.reviews.columns.status')}>{info.stnm}</Row>
							</section>
							<section>
								<h3 className="mb-1 text-xs font-semibold tracking-[0.04em] text-foreground-subtle">
									{t('catalog.reviews.details.target')}
								</h3>
								<div className="flex items-center gap-3 py-1.5">
									<ProductThumb src={info.to_img ?? info.pimg} alt={info.to_nm} size={48} />
									<div className="min-w-0">
										<Pill variant={reviewTargetVariant(info.to_ent)}>{reviewTargetTypeLabel(t, info.to_ent, info.to_ent_nm)}</Pill>
										<div className="mt-0.5 flex items-center gap-1">
											<span className="truncate font-medium text-foreground">{reviewTargetName(info.to_nm || info.pnm, info.to_id)}</span>
											{targetUrl && (
												<a
													href={targetUrl}
													target="_blank"
													rel="noreferrer"
													title={t('catalog.reviews.openTarget')}
													aria-label={t('catalog.reviews.openTarget')}
													className="shrink-0 text-foreground-subtle hover:text-foreground">
													<ExternalLink size={12} />
												</a>
											)}
										</div>
										{(info.to_cd || info.pcd) && (
											<div className="truncate font-mono text-[11px] text-foreground-muted">{info.to_cd || info.pcd}</div>
										)}
									</div>
								</div>
								{isProductTarget(info.to_ent) && (
									<>
										<Row label={t('catalog.reviews.details.category')}>{info.pcat}</Row>
										<Row label={t('catalog.reviews.details.publisher')}>{info.ppubnm}</Row>
									</>
								)}
							</section>
							{reviewAuthorKind(kind, info) === 'customer' ? (
								<section>
									<h3 className="mb-1 text-xs font-semibold tracking-[0.04em] text-foreground-subtle">
										{t('catalog.reviews.details.customer')}
									</h3>
									<Row label={t('catalog.reviews.details.name')}>{`${info.cnm ?? ''} ${info.csnm ?? ''}`.trim() || '—'}</Row>
									<Row label={t('catalog.reviews.export.email')}>{info.ceml}</Row>
									<Row label={t('catalog.reviews.details.registered')}>{formatDate(info.crgdt)}</Row>
									<Row label={t('catalog.reviews.details.orders')}>{info.cordrs ?? 0}</Row>
									<Row label={t('catalog.reviews.details.reviews')}>{info.crevs ?? 0}</Row>
								</section>
							) : (
								<section>
									<h3 className="mb-1 text-xs font-semibold tracking-[0.04em] text-foreground-subtle">
										{t('catalog.reviews.author.editorial')}
									</h3>
									<Row label={t('catalog.reviews.fields.contributor')}>{info.from_nm || '—'}</Row>
									{row?.reviewer && <Row label={t('catalog.reviews.fields.reviewer')}>{row.reviewer}</Row>}
									{row?.source && <Row label={t('catalog.reviews.fields.source')}>{row.source}</Row>}
								</section>
							)}
						</>
					)}
				</div>
			</SheetContent>
		</Sheet>
	);
}
